import { createFileRoute } from '@tanstack/react-router'
import { useNavTitle } from '../lib/nav'
import { useQuery } from '@tanstack/react-query'
import { PUBLISHED_THROUGH, publishedThroughWeek, rankLabel, STANDINGS_COLLAPSED_LABELS, STANDINGS_COLUMN_HEADERS } from '@region13/shared-types'
import { api, type CoachTeamWithGames, type CoachStandingsResponse, type StandingsTeamRow } from '../lib/api'
import { Card } from '../components/ui/Card'
import { ListSkeleton } from '../components/ui/ListSkeleton'
import { TeamScope } from '../components/TeamScope'
import { STANDINGS_NOT_AVAILABLE_MESSAGE, STANDINGS_NOT_PUBLISHED_MESSAGE } from '../lib/standingsCopy'

export const Route = createFileRoute('/standings')({ component: Standings })

/** The record cell: "4 1 0" above the 480px container breakpoint (against the
 *  "W T L" column header), "4 W 1 T 0 L" below it, where the header itself is
 *  hidden — see `.standings-unit` in index.css. */
function RecordCell({ t }: { t: Pick<StandingsTeamRow, 'won' | 'tied' | 'lost'> }) {
  return (
    <>
      {t.won}<span className="standings-unit"> W</span>{' '}
      {t.tied}<span className="standings-unit"> T</span>{' '}
      {t.lost}<span className="standings-unit"> L</span>
    </>
  )
}

/** A collapsed-view-only column label ("Scored", "Allowed", "Sportsmanship",
 *  "Referee") — hidden above the 480px container breakpoint (against the real
 *  column header) and shown below it, where the header itself is hidden.
 *
 *  ⚠️ FIX ROUND 1: the container query alone left GS/GA/Referee as bare
 *  numbers under 480px, with nothing to say which was which — the identical
 *  defect class the public page (`apps/stats/src/lib/standingsPage.ts`) was
 *  specifically reviewed into fixing with its own `::before` labels, and only
 *  its W/T/L half of that fix crossed over here the first time.
 *
 *  Real DOM text, not CSS `::before` content like the public page's version:
 *  this is React, so a visible-but-hideable span is free, and it's what lets
 *  `standings.test.tsx` assert the label is actually IN the render rather
 *  than merely that a CSS rule exists for it — a CSS-content assertion isn't
 *  available here since these unit tests don't load a stylesheet into jsdom.
 *
 *  ⚠️ FIX ROUND 2, PART 1: `aria-hidden="true"`. The `<th>` for the
 *  sportsmanship and referee columns already carries this exact word to
 *  assistive tech (visually hidden, not `aria-hidden` — see the header row
 *  below), so a real span with the same text is a GUARANTEE of a double
 *  announcement in table-navigation mode ("Sportsmanship, Sportsmanship 28 /
 *  30"), where `::before` content only made that a maybe. Hiding the span
 *  from the accessibility tree leaves it as a sighted-only affordance and the
 *  `<th>` as the one thing a screen reader says.
 *
 *  ⚠️ FIX ROUND 2, PART 2: the word itself comes from
 *  `STANDINGS_COLLAPSED_LABELS` (`@region13/shared-types`), not a literal —
 *  see that constant's doc comment for why a second hard-coded copy here was
 *  the wrong call. The trailing space is THIS component's concern: the
 *  constant carries none, and JSX's `{text} ` is what adds it back as a
 *  sibling text node. */
function CollapsedLabel({ text }: { text: string }) {
  return <span className="standings-label" aria-hidden="true">{text} </span>
}

/** One ranked row. `ownExternalId` decides the highlight — the coach's own
 *  team in this division's table, never inferred from position. */
function StandingsRow({ t, ownExternalId }: { t: StandingsTeamRow; ownExternalId: string }) {
  const isOwn = t.externalId === ownExternalId
  return (
    <tr className={isOwn ? 'standings-row-own' : undefined}>
      <td className="standings-col-pos">{rankLabel(t)}</td>
      {/* The DESIGNATION, never the InLeague friendly name — the same
          identifier the team picker, the schedule site and the public
          standings tables use. See `StandingsTeamRow` in
          `@region13/core/dao/standings` for the drift this closed. */}
      <td className="standings-col-team">{t.externalId}</td>
      <td className="standings-col-gp">{t.played}</td>
      <td className="standings-col-rec"><RecordCell t={t} /></td>
      <td className="standings-col-gf"><CollapsedLabel text={STANDINGS_COLLAPSED_LABELS.scored} />{t.goalsFor}</td>
      <td className="standings-col-ga"><CollapsedLabel text={STANDINGS_COLLAPSED_LABELS.allowed} />{t.goalsAgainst}</td>
      {/* ⚠️ FIX ROUND 3: the " pts" suffix. Below the breakpoint the header row
          is hidden, so this read `1  B10-15  13` with the 13 unlabeled — the
          public page appends the same word there and this surface did not.
          Same `aria-hidden` reasoning as `CollapsedLabel`: the <th> already
          says "Points" to assistive tech. */}
      <td className="standings-col-pts">
        {t.points}<span className="standings-pts-unit" aria-hidden="true"> {STANDINGS_COLLAPSED_LABELS.points}</span>
      </td>
      {/* Read sportsmanship/sportsmanshipPossible directly rather than parsing
          a shared "28 / 30" label string back apart — Task 5's review finding
          this task's brief calls out by name. That helper has since been
          deleted: every surface declined it, correctly. */}
      <td className="standings-col-sport">
        <CollapsedLabel text={STANDINGS_COLLAPSED_LABELS.sportsmanship} />{t.sportsmanship} <span className="standings-sub">/ {t.sportsmanshipPossible}</span>
      </td>
      <td className="standings-col-ref"><CollapsedLabel text={STANDINGS_COLLAPSED_LABELS.referee} />{t.refereePoints}</td>
    </tr>
  )
}

function PoolTable({ pool, ownExternalId }: { pool: { pool: string | null; teams: StandingsTeamRow[] }; ownExternalId: string }) {
  return (
    <div className="standings-table-wrap">
      <table className="standings-table">
        {pool.pool !== null && (
          <caption className="mb-1 text-left text-sm font-semibold text-ink">Pool {pool.pool}</caption>
        )}
        {/* ⚠️ THE WORDS COME FROM `@region13/shared-types`, not from here. They
            were a private literal copy, with a second one on the public page
            — the same drift class the collapsed labels were already extracted
            for. `record` carries real U+00A0 characters rather than `&nbsp;`,
            which is what lets one constant serve both JSX and raw HTML. */}
        <thead>
          <tr>
            <th className="standings-col-pos" scope="col">{STANDINGS_COLUMN_HEADERS.position}</th>
            <th className="standings-col-team" scope="col">{STANDINGS_COLUMN_HEADERS.team}</th>
            <th className="standings-col-gp" scope="col">{STANDINGS_COLUMN_HEADERS.played}</th>
            <th className="standings-col-rec" scope="col">{STANDINGS_COLUMN_HEADERS.record}</th>
            <th className="standings-col-gf" scope="col">{STANDINGS_COLUMN_HEADERS.goalsFor}</th>
            <th className="standings-col-ga" scope="col">{STANDINGS_COLUMN_HEADERS.goalsAgainst}</th>
            <th className="standings-col-pts" scope="col">{STANDINGS_COLUMN_HEADERS.points}</th>
            <th className="standings-col-sport" scope="col">{STANDINGS_COLUMN_HEADERS.sportsmanship}</th>
            <th className="standings-col-ref" scope="col">{STANDINGS_COLUMN_HEADERS.referee}</th>
          </tr>
        </thead>
        <tbody>
          {pool.teams.map((t) => <StandingsRow key={t.externalId} t={t} ownExternalId={ownExternalId} />)}
        </tbody>
      </table>
    </div>
  )
}

/** One team's division standings card: the whole table for that division, not
 *  a summary line — a coach's question is "where are we", and only the table
 *  answers it (task-6 brief). Reads `GET /api/coach/teams/:ext/standings`,
 *  which is `@region13/core/dao/standings`'s `loadStandings` under
 *  per-team authz, so this can never show a number the public tables
 *  disagree with. */
export function TeamStandings({ team }: { team: CoachTeamWithGames }) {
  const q = useQuery({
    queryKey: ['standings', team.externalId],
    queryFn: () => api.standings(team.externalId),
  })

  return (
    <Card as="section">
      <div>
        <h2 className="font-semibold text-ink">{team.division}</h2>
      </div>
      {q.isLoading ? (
        <ListSkeleton label="Loading standings" />
      ) : q.isError ? (
        <p role="alert" className="text-sm text-brand-red-dark">Couldn't load standings. Please try again.</p>
      ) : (
        <StandingsBody data={q.data!} ownExternalId={team.externalId} />
      )}
    </Card>
  )
}

function StandingsBody({ data, ownExternalId }: { data: CoachStandingsResponse; ownExternalId: string }) {
  if (data.publishedThrough === null) return <p className="text-sm text-ink-soft">{STANDINGS_NOT_PUBLISHED_MESSAGE}</p>
  if (data.division === null) return <p className="text-sm text-ink-soft">{STANDINGS_NOT_AVAILABLE_MESSAGE}</p>
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-soft">{PUBLISHED_THROUGH} <span className="font-medium text-ink">{publishedThroughWeek(data.publishedThrough)}</span></p>
      {data.division.pools.map((pool) => (
        <PoolTable key={pool.pool ?? ''} pool={pool} ownExternalId={ownExternalId} />
      ))}
    </div>
  )
}

function Standings() {
  return (
    <TeamScope
      heading={useNavTitle()}
      emptyText="No teams found for your account this season."
      hint="Pick a team to see its division standings."
    >
      {(team) => <TeamStandings team={team} />}
    </TeamScope>
  )
}
